import React, { useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import SubPage from '#/common/components/SubPage';
import useCapitalNeedQuery from '../hooks/useCapitalNeedQuery';
import CapitalNeedForm from '../components/CapitalNeedForm/CapitalNeedForm';
import { CapitalNeed } from '#/graphql/types';
import useAssetQuery from '#/modules/asset/hooks/useAssetQuery';
import getFormTitle from '#/common/utils/getFormTitle';
import { CAPITAL_NEED_STATUS } from '../utils/constants';
import useNotFoundGuard from '#/common/hooks/useNotFoundGuard';
import RecordNotFound from '#/app/pages/errors/RecordNotFound';

export interface CapitalNeedParams {
  readonly id?: string;
}

const CapitalNeedPage: React.FC = () => {
  const { id } = useParams() as CapitalNeedParams;
  const [searchParams, setSearchParams] = useSearchParams();
  const assetId = searchParams.get('assetId');
  const tenantIdFromParams = searchParams.get('tenantId');

  const { data: queryData, loading: queryLoading } = useCapitalNeedQuery(id);
  const capitalNeedData = queryData?.getCapitalNeed?.data;
  const isNotFound = useNotFoundGuard({ loading: queryLoading, id, entity: capitalNeedData });
  const capitalNeed: CapitalNeed = capitalNeedData ?? ({} as CapitalNeed);

  const formKeyRef = useRef<string | null>(null);
  if (capitalNeed?.id) {
    formKeyRef.current = capitalNeed.updatedAt ?? capitalNeed.id ?? null;
  }

  const { data: assetQueryData, loading: assetQueryLoading } = useAssetQuery(assetId ?? '', {
    skip: !!tenantIdFromParams || !assetId,
  });
  const tenantIdFromAsset = assetQueryData?.getAsset?.data?.tenantId;

  if (!tenantIdFromParams && tenantIdFromAsset) {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tenantId', tenantIdFromAsset);
    setSearchParams(newSearchParams, { replace: true });
  }

  const title = getFormTitle(id, capitalNeed.displayNumber, 'Capital Need');
  const loading = queryLoading || assetQueryLoading;

  if (isNotFound) {
    return (
      <SubPage title={title} showGoBackButton type="form" isLoading={false}>
        <RecordNotFound backUrl="/assets/list" />
      </SubPage>
    );
  }

  return (
    <SubPage title={title} showGoBackButton type="form" isLoading={loading}>
      <CapitalNeedForm
        key={`capital-need-${id}-${formKeyRef.current}`}
        title={title}
        capitalNeed={
          id === 'new' || !id
            ? ({
                assetId,
                tenantId: tenantIdFromParams || tenantIdFromAsset,
                status: CAPITAL_NEED_STATUS.DRAFT.value,
              } as CapitalNeed)
            : capitalNeed
        }
      />
    </SubPage>
  );
};

export default React.memo(CapitalNeedPage);
